import { Component, Inject, OnInit } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { LabelBindingModel, LabelModel } from "src/app/models/api/common";
import { StoreService } from "src/app/services/infra/store/store.service";
import { CreateLabelDialogComponent, CreateLabelDialogInputData } from "../create-label-dialog/create-label-dialog.component";
import { PermissionsService } from "src/app/services/infra/permissionsService/permissions.service";
import { PermissionEnum } from "src/app/models/auth0/permission.enum";
import { NotificationsService } from "src/app/services/infra/notifications/notifications.service";

export class LabelsDialogInputData {
    public existingSelectedLabels: LabelBindingModel[];
    public titleLabelPath: string;
}

export class LabelsDialogOutputData {
    public labelsToBind: LabelModel[];
    public note: string;
}

@Component({
    selector: "app-labels-dialog",
    templateUrl: "./labels-dialog.component.html",
    styleUrls: ["./labels-dialog.component.scss"],
})
export class LabelsDialogComponent implements OnInit {
    public allLabels: LabelModel[] = [];
    public systemLabels: LabelModel[] = [];
    public systemLabelsShown: LabelModel[] = [];
    public customLabels: LabelModel[] = [];
    public customLabelsShown: LabelModel[] = [];
    public waitingForResponse = false;
    public selectedLabels: Set<LabelModel> = new Set();
    public isOperator = false;
    private _maxLabelCount = 5;
    private _maxLabelsPerViolation = 10;
    private _alreadyBoundLabelIds: string[] = [];

    constructor(
        public dialogRef: MatDialogRef<LabelsDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public inputData: LabelsDialogInputData,
        private _store: StoreService,
        public matDialog: MatDialog,
        public permissionsService: PermissionsService,
        private _notificationService: NotificationsService,
    ) {
        this._store.labels$.subscribe((labels: LabelModel[]) => {
            let flag = false;
            labels.forEach((label: LabelModel) => {
                if (this.allLabels.find((labelExist: LabelModel) => label.id === labelExist.id)) return;
                flag = true;
                if (label.id.startsWith("BUILTIN_")) this.systemLabels.push(label);
                else this.customLabels.push(label);
            });
            if (!flag) return;
            this.allLabels = labels;

            this.searchSubmitted();
        });
        this.isOperator = this.permissionsService.hasPermissions(PermissionEnum.operator);
    }

    public ngOnInit(): void {
        this._alreadyBoundLabelIds = this.inputData.existingSelectedLabels.map((labelBinding: LabelBindingModel) => labelBinding.label.id);

        this._alreadyBoundLabelIds.forEach((labelId: string) => {
            const label = this.allLabels.find((label: LabelModel | undefined) => label?.id === labelId);
            if (label) {
                this.selectedLabels.add(label);
            }
        });
    }

    public isLabelAlreadyBound = (label: LabelModel) => this._alreadyBoundLabelIds.includes(label.id);

    public labelSelected(label: LabelModel) {
        this.selectedLabels.add(label);
        if (this.isMaxLabelsReached()) {
            this._notificationService.info(`You can select up to ${this._maxLabelsPerViolation} labels`);
        }
    }

    public labelUnselected(label: LabelModel) {
        this.selectedLabels.delete(label);
    }

    public saveLabels(noteContent: string): void {
        this.waitingForResponse = true;
        this.dialogRef.close({
            labelsToBind: Array.from(this.selectedLabels.values()),
            note: noteContent,
        } as LabelsDialogOutputData);
    }

    public createNewLabelClick(): void {
        if (!this.isOperator) return;
        this.dialogRef.close();
        this.matDialog.open(CreateLabelDialogComponent, {
            data: { allLabels: this.allLabels } as CreateLabelDialogInputData,
            disableClose: false,
            autoFocus: false,
            panelClass: "sidepanel-dialog-container",
            width: "350px",
        });
    }

    public searchSubmitted(searchValue?: string): void {
        if (!searchValue) {
            this.systemLabelsShown = this.systemLabels;
            this.customLabelsShown = this.customLabels;
            return;
        }
        this.systemLabelsShown = this.systemLabels.filter((label: LabelModel) =>
            label.displayName.toLowerCase().startsWith(searchValue.toLowerCase()),
        );
        this.customLabelsShown = this.customLabels.filter((label: LabelModel) =>
            label.displayName.toLowerCase().startsWith(searchValue.toLowerCase()),
        );
    }

    public isMaxLabelsReached(): boolean {
        return this.selectedLabels.size + this._alreadyBoundLabelIds.length > this._maxLabelsPerViolation;
    }

    public onClose(): void {
        this.dialogRef.close();
    }

    public isLabelSelected(label: LabelModel): boolean {
        return this.selectedLabels.has(label);
    }

    public getCustomLabelHeight(): string {
        const amountOfRows = Math.min(this.customLabelsShown.length, this._maxLabelCount);
        const rowHeight = 42; // Height of row is 32px and 10px gap between rows
        return (amountOfRows * rowHeight).toString() + "px";
    }

    public isSelectedLabelsEqualToAlreadyBoundLabels(): boolean {
        if (this.selectedLabels.size !== this._alreadyBoundLabelIds.length) {
            return false;
        }

        for (const value of this.selectedLabels) {
            if (!this._alreadyBoundLabelIds.includes(value.id)) {
                return false;
            }
        }

        return true;
    }
}
